import React, { useState, useEffect } from "react";
import { Card, Button, message, Tag, Divider, Spin, Row, Col, Statistic, Input, InputNumber, Form, Table } from "antd";
import { getRequest, putRequest, postRequest } from "../service/index";
import {
  User,
  Calendar,
  DollarSign,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit3,
  Plus
} from "lucide-react";
import { getEmployeeData } from "../utils/localStorage";
import { nowInUserTz, formatDate } from "../utils/dateTime";

const PayrollEdit = ({ onClose, payrollId, readOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [isUpdatingIndividualDeductions, setIsUpdatingIndividualDeductions] = useState(false);
  const [savingAndProcessing, setSavingAndProcessing] = useState(false);
  const [showAttendanceLog, setShowAttendanceLog] = useState(false);
  const [attendanceFilter, setAttendanceFilter] = useState(null);
  const currency = getEmployeeData()?.currency;
  const showDecimals = getEmployeeData()?.decimal !== false;
  const moneyPrecision = showDecimals ? 2 : 0;

  // Auto-calculate net salary when total allowances or deductions change
  const calculateNetSalary = (totalAllowances, deductions) => {
    return (totalAllowances || 0) - (deductions || 0);
  };

  // Function to add new allowance
  const addNewAllowance = () => {
    const newAllowance = {
      name: '',
      amount: 0,
      _id: `new_allowance_${Date.now()}`
    };
    setAllowances([...allowances, newAllowance]);
  };

  // Function to add new deduction
  const addNewDeduction = () => {
    const newDeduction = {
      name: '',
      amount: 0,
      _id: `new_deduction_${Date.now()}`
    };
    setDeductions([...deductions, newDeduction]);
  };

  // Function to remove allowance
  const removeAllowance = (index) => {
    const newAllowances = allowances.filter((_, i) => i !== index);
    setAllowances(newAllowances);
    
    // Update net salary
    const totalAllowances = allowances.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + (item.amount || 0), 0);
    const netSalary = calculateNetSalary(totalAllowances, totalDeductions);
    form.setFieldValue('netSalary', netSalary);
  };

  // Function to remove deduction
  const removeDeduction = (index) => {
    const newDeductions = deductions.filter((_, i) => i !== index);
    setDeductions(newDeductions);
    
    // Update form fields immediately
    const newTotalDeductions = newDeductions.reduce((sum, item) => sum + (item.amount || 0), 0);
    form.setFieldValue('totalDeductions', newTotalDeductions);

    // Update net salary
    const totalAllowances = allowances.reduce((sum, item) => sum + (item.amount || 0), 0);
    const netSalary = calculateNetSalary(totalAllowances, newTotalDeductions);
    form.setFieldValue('netSalary', netSalary);
  };

  // Function to handle individual allowance amount change
  const handleAllowanceAmountChange = (index, value) => {
    const newAllowances = [...allowances];
    newAllowances[index] = { ...newAllowances[index], amount: value };

    // Update state
    setAllowances(newAllowances);

    // Update form fields immediately based on allowance type
    if (index === 0) {
      // First allowance is Basic Salary
      form.setFieldValue('basicSalary', value || 0);
    } else if (index === 1) {
      // Second allowance is Overtime
      form.setFieldValue('overtimeSalary', value || 0);
    }

    // Update net salary
    const totalAllowances = newAllowances.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalDeductions = deductions.reduce((sum, item) => sum + (item.amount || 0), 0);
    const netSalary = calculateNetSalary(totalAllowances, totalDeductions);
    form.setFieldValue('netSalary', netSalary);
  };

  // Function to handle individual allowance name change
  const handleAllowanceNameChange = (index, value) => {
    const newAllowances = [...allowances];
    newAllowances[index] = { ...newAllowances[index], name: value };
    setAllowances(newAllowances);
  };

  // Function to handle individual deduction amount change
  const handleDeductionAmountChange = (index, value) => {
    const newDeductions = [...deductions];
    newDeductions[index] = { ...newDeductions[index], amount: value };

    // Calculate new total immediately
    const newTotalDeductions = newDeductions.reduce((sum, item) => sum + (item.amount || 0), 0);

    // Update state
    setDeductions(newDeductions);

    // Update form fields immediately
    form.setFieldValue('totalDeductions', newTotalDeductions);

    // Update net salary
    const totalAllowances = allowances.reduce((sum, item) => sum + (item.amount || 0), 0);
    const netSalary = calculateNetSalary(totalAllowances, newTotalDeductions);
    form.setFieldValue('netSalary', netSalary);
  };

  // Function to handle individual deduction name change
  const handleDeductionNameChange = (index, value) => {
    const newDeductions = [...deductions];
    newDeductions[index] = { ...newDeductions[index], name: value };
    setDeductions(newDeductions);
  };

  useEffect(() => {
    if (payrollId) {
      fetchPayrollDetails();
    }
  }, [payrollId]);

  const fetchPayrollDetails = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`/api/monthlysalery/${payrollId}`);
      if (response?.success) {
        setPayrollData(response.data);
        // Initialize form with current data
        const basicSalary = response.data.payrollDetails?.salaryCalculations?.basicSalary || 0;
        const overtimeSalary = response.data.payrollDetails?.salaryCalculations?.overtimeSalary || 0;
        const totalAllowances = response.data.payrollDetails?.salaryCalculations?.totalAllowances || 0;
        const totalDeductions = response.data.payrollDetails?.salaryCalculations?.totalDeductions || 0;
        const netSalary = calculateNetSalary(totalAllowances, totalDeductions);

        // Set allowances and deductions state
        setAllowances(response.data.payrollDetails?.allowances || []);
        setDeductions(response.data.payrollDetails?.deductions || []);

        form.setFieldsValue({
          basicSalary: basicSalary,
          overtimeSalary: overtimeSalary,
          totalAllowances: totalAllowances,
          totalDeductions: totalDeductions,
          netSalary: netSalary
        });
      } else {
        message.error("Failed to fetch payroll details");
      }
    } catch (error) {
      console.error("Error fetching payroll details:", error);
      message.error("Failed to fetch payroll details");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Sync all form fields with current state before saving
      const totalDeductions = deductions.reduce((sum, item) => sum + (item.amount || 0), 0);
      const totalAllowances = allowances.reduce((sum, item) => sum + (item.amount || 0), 0);

      // Update form fields to ensure they have the latest values
      form.setFieldsValue({
        totalAllowances: totalAllowances,
        totalDeductions: totalDeductions,
        netSalary: calculateNetSalary(totalAllowances, totalDeductions)
      });

      // Get current values from state and form
      const basicSalary = form.getFieldValue('basicSalary') || 0;
      const overtimeSalary = form.getFieldValue('overtimeSalary') || 0;
      const netSalary = calculateNetSalary(totalAllowances, totalDeductions);

      // Use the state variables for allowances and deductions
      const allowancesForPayload = allowances.map((allowance) => ({
        name: allowance.name,
        amount: allowance.amount,
        ...(allowance._id && !allowance._id.startsWith('new_') && { _id: allowance._id })
      }));

      const deductionsForPayload = deductions.map((deduction) => ({
        name: deduction.name,
        amount: deduction.amount,
        ...(deduction._id && !deduction._id.startsWith('new_') && { _id: deduction._id })
      }));

      const updateData = {
        status: payrollData.payrollDetails?.status,
        salaryCalculations: {
          basicSalary: basicSalary,
          overtimeSalary: overtimeSalary,
          totalAllowances: totalAllowances,
          totalDeductions: totalDeductions,
          netSalary: netSalary
        },
        allowances: allowancesForPayload,
        deductions: deductionsForPayload
      };

      const response = await putRequest(`/api/monthlysalery/${payrollId}`, updateData);

      if (response?.success) {
        message.success("Payroll updated successfully!");
        setIsEditing(false);
        onClose();
      } else {
        message.error(response?.message || "Failed to update payroll");
      }
    } catch (error) {
      console.error("Error updating payroll:", error);
      message.error("Failed to update payroll");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndProcess = async () => {
    try {
      setSavingAndProcessing(true);

      // First save the payroll data
      const totalDeductions = deductions.reduce((sum, item) => sum + (item.amount || 0), 0);
      const totalAllowances = allowances.reduce((sum, item) => sum + (item.amount || 0), 0);

      form.setFieldsValue({
        totalAllowances: totalAllowances,
        totalDeductions: totalDeductions,
        netSalary: calculateNetSalary(totalAllowances, totalDeductions)
      });

      const basicSalary = form.getFieldValue('basicSalary') || 0;
      const overtimeSalary = form.getFieldValue('overtimeSalary') || 0;
      const netSalary = calculateNetSalary(totalAllowances, totalDeductions);

      const allowancesForPayload = allowances.map((allowance) => ({
        name: allowance.name,
        amount: allowance.amount,
        ...(allowance._id && !allowance._id.startsWith('new_') && { _id: allowance._id })
      }));

      const deductionsForPayload = deductions.map((deduction) => ({
        name: deduction.name,
        amount: deduction.amount,
        ...(deduction._id && !deduction._id.startsWith('new_') && { _id: deduction._id })
      }));

      const updateData = {
        status: payrollData.payrollDetails?.status,
        salaryCalculations: {
          basicSalary: basicSalary,
          overtimeSalary: overtimeSalary,
          totalAllowances: totalAllowances,
          totalDeductions: totalDeductions,
          netSalary: netSalary
        },
        allowances: allowancesForPayload,
        deductions: deductionsForPayload
      };

      const saveResponse = await putRequest(`/api/monthlysalery/${payrollId}`, updateData);

      if (saveResponse?.success) {
        // Determine next status based on current status
        let nextStatus = '';
        if (payrollData.payrollDetails?.status === 'pending') {
          nextStatus = 'processed';
        } else if (payrollData.payrollDetails?.status === 'processed') {
          nextStatus = 'approved';
        }

        if (nextStatus) {
          // Call bulk status API to update status
          const statusPayload = {
            status: nextStatus,
            salaryIds: [payrollId]
          };

          const statusResponse = await postRequest('/api/monthlysalery/bulk-status', statusPayload);

          if (statusResponse?.success) {
            message.success(`Payroll saved and ${nextStatus} successfully!`);
            setIsEditing(false);
            onClose();
          } else {
            message.error(statusResponse?.message || `Failed to update status to ${nextStatus}`);
          }
        }
      } else {
        message.error(saveResponse?.message || "Failed to save payroll");
      }
    } catch (error) {
      console.error("Error saving and processing payroll:", error);
      message.error("Failed to save and process payroll");
    } finally {
      setSavingAndProcessing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    const basicSalary = payrollData.payrollDetails?.salaryCalculations?.basicSalary || 0;
    const overtimeSalary = payrollData.payrollDetails?.salaryCalculations?.overtimeSalary || 0;
    const totalAllowances = payrollData.payrollDetails?.salaryCalculations?.totalAllowances || 0;
    const totalDeductions = payrollData.payrollDetails?.salaryCalculations?.totalDeductions || 0;
    const netSalary = calculateNetSalary(totalAllowances, totalDeductions);

    // Reset allowances and deductions state to original data
    setAllowances([...payrollData.payrollDetails?.allowances || []]);
    setDeductions([...payrollData.payrollDetails?.deductions || []]);

    form.setFieldsValue({
      basicSalary: basicSalary,
      overtimeSalary: overtimeSalary,
      totalAllowances: totalAllowances,
      totalDeductions: totalDeductions,
      netSalary: netSalary
    });
  };

  const formatCurrency = (amount) => {
    return `${currency || 'RS'} ${Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: moneyPrecision,
      maximumFractionDigits: moneyPrecision
    })}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'green';
      case 'pending':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return <CheckCircle size={16} />;
      case 'pending':
        return <AlertCircle size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  const getAttendanceStatusColor = (status) => {
    switch (status) {
      case 'Present':
        return 'green';
      case 'Late':
        return 'orange';
      case 'Missing':
        return 'red';
      case 'Absent':
        return 'red';
      case 'Weekend':
        return 'default';
      case 'Incomplete':
        return 'gold';
      case 'Early Out':
        return 'purple';
      case 'Late & Early Out':
        return 'magenta';
      case 'Leave':
        return 'blue';
      case 'Holiday':
        return 'cyan';
      case 'Half Day':
        return 'lime';
      default:
        return 'default';
    }
  };

  const getLogDeduction = (log) => {
    if (log.status === 'Missing') return log.perDaySalary || 0;
    if (log.status === 'Late & Early Out') {
      return (log.lateDeduction || 0) + (log.earlyOutDeduction || 0);
    }
    return log.lateDeduction || log.earlyOutDeduction || log.incompleteDeduction || 0;
  };

  const attendanceFilterOptions = [
    { key: 'present', label: 'Present', color: 'green' },
    { key: 'missing', label: 'Missing', color: 'red' },
    { key: 'absent', label: 'Absent', color: 'volcano' },
    { key: 'incomplete', label: 'Incomplete', color: 'gold' },
    { key: 'late', label: 'Late', color: 'orange' },
    { key: 'early', label: 'Early Out', color: 'purple' },
    { key: 'late-early', label: 'Late & Early', color: 'magenta' },
  ];

  const matchesAttendanceFilter = (log) => {
    if (!attendanceFilter) return true;
    switch (attendanceFilter) {
      case 'present':
        return log.status === 'Present';
      case 'missing':
        return log.status === 'Missing';
      case 'absent':
        return log.status === 'Absent';
      case 'incomplete':
        return log.status === 'Incomplete';
      case 'late':
        return log.status === 'Late';
      case 'early':
        return log.status === 'Early Out';
      case 'late-early':
        return log.status === 'Late & Early Out';
      default:
        return true;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  if (!payrollData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No payroll data found</p>
      </div>
    );
  }

  const { employeeDetails, payrollDetails } = payrollData;
  const attendanceSummary = payrollDetails?.attendanceSummary || {};
  const combinedLeaveDays = (attendanceSummary.leaveDays || 0) + (attendanceSummary.halfDays || 0);
  const filteredAttendanceLog = (payrollDetails?.attendanceLog || []).filter(matchesAttendanceFilter);

  const attendanceLogColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => formatDate(date, 'DD/MM/YYYY'),
    },
    {
      title: 'Day',
      dataIndex: 'date',
      key: 'day',
      render: (date) => formatDate(date, 'dddd'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getAttendanceStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: 'Check In',
      dataIndex: 'checkIn',
      key: 'checkIn',
      render: (time) => time || '-',
    },
    {
      title: 'Check Out',
      dataIndex: 'checkOut',
      key: 'checkOut',
      render: (time) => time || '-',
    },
    {
      title: 'Working Hours',
      dataIndex: 'workingHour',
      key: 'workingHour',
      render: (hours) => hours || '0:00',
    },
    {
      title: 'Overtime',
      dataIndex: 'overtime',
      key: 'overtime',
      render: (overtime) => overtime || '0:00',
    },
    {
      title: 'Per Day Salary',
      dataIndex: 'perDaySalary',
      key: 'perDaySalary',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Deductions',
      key: 'deductions',
      render: (_, log) => formatCurrency(getLogDeduction(log)),
    },
  ];

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <Form form={form} layout="vertical">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="text-blue-600" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {employeeDetails?.employeeName}
                </h2>
                <p className="text-gray-600 text-sm">
                  {employeeDetails?.employeeCode} • {employeeDetails?.designation}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Tag
                color={getStatusColor(payrollDetails?.status)}
                icon={getStatusIcon(payrollDetails?.status)}
                className="text-sm font-medium"
              >
                {payrollDetails?.status?.toUpperCase()}
              </Tag>
              {!isEditing && !readOnly && (
                <Button
                  type="primary"
                  icon={<Edit3 size={16} />}
                  onClick={handleEdit}
                  className="bg-primary text-white"
                >
                  Edit
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar size={16} />
            <span>
              {nowInUserTz().year(payrollDetails?.year).month((payrollDetails?.month || 1) - 1).format('MMMM YYYY')}
            </span>
          </div>
        </div>

        {/* Editable Summary Statistics */}
        <Row gutter={[16, 16]} className="mb-6">
          <Col span={6}>
            <Card className="text-center border-green-200 bg-green-50">
              <Statistic
                title="Total Allowances"
                value={form.getFieldValue('totalAllowances') ?? payrollDetails?.salaryCalculations?.totalAllowances ?? 0}
                precision={moneyPrecision}
                valueStyle={{ color: '#059669', fontSize: '18px', fontWeight: 'bold' }}
                prefix={`${currency || 'RS'} `}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="text-center border-blue-200 bg-blue-50">
              <Statistic
                title="Overtime"
                value={form.getFieldValue('overtimeSalary') ?? payrollDetails?.salaryCalculations?.overtimeSalary ?? 0}
                precision={moneyPrecision}
                valueStyle={{ color: '#2563eb', fontSize: '18px', fontWeight: 'bold' }}
                prefix={`${currency || 'RS'} `}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="text-center border-orange-200 bg-orange-50">
              <Statistic
                title="Deductions"
                value={
                  form.getFieldValue('totalDeductions') ??
                  payrollDetails?.salaryCalculations?.totalDeductions ??
                  0
                }
                precision={moneyPrecision}
                valueStyle={{ color: '#ea580c', fontSize: '18px', fontWeight: 'bold' }}
                prefix={`${currency || 'RS'} `}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card className="text-center border-purple-200 bg-purple-50">
              <Statistic
                title="Net Salary"
                value={
                  form.getFieldValue('netSalary') ??
                  payrollDetails?.salaryCalculations?.netSalary ??
                  0
                }
                precision={moneyPrecision}
                valueStyle={{ color: '#7c3aed', fontSize: '18px', fontWeight: 'bold' }}
                prefix={`${currency || 'RS'} `}
              />
            </Card>
          </Col>
        </Row>

        {/* Attendance Summary (Read-only) */}
        <Card title="Attendance Summary " className="mb-3">
          <Row gutter={[16, 16]} className="w-full">
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {attendanceSummary.totalDays || 0}
                </div>
                <div className="text-sm text-gray-600">Total Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-500">
                  {attendanceSummary.weekendDays || 0}
                </div>
                <div className="text-sm text-gray-600">Weekend Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {attendanceSummary.presentDays || 0}
                </div>
                <div className="text-sm text-gray-600">Present Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">
                  {attendanceSummary.missingDays || 0}
                </div>
                <div className="text-sm text-gray-600">Missing Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {attendanceSummary.absentDays || 0}
                </div>
                <div className="text-sm text-gray-600">Absent Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-500">
                  {attendanceSummary.incompleteDays || 0}
                </div>
                <div className="text-sm text-gray-600">Incomplete Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {combinedLeaveDays}
                </div>
                <div className="text-sm text-gray-600">Leave Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {attendanceSummary.lateDays || 0}
                </div>
                <div className="text-sm text-gray-600">Late Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {attendanceSummary.holidayDays || 0}
                </div>
                <div className="text-sm text-gray-600">Holiday Days</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-pink-600">
                  {attendanceSummary.earlyOutDays || 0}
                </div>
                <div className="text-sm text-gray-600">Early Out</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {attendanceSummary.totalOvertime?.toFixed(1) || 0}
                </div>
                <div className="text-sm text-gray-600">Overtime Hours</div>
              </div>
            </Col>
            <Col span={6}>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {attendanceSummary.totalWorkingHours?.toFixed(1) || 0}
                </div>
                <div className="text-sm text-gray-600">Working Hours</div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* Attendance Log */}
        <Card
          title="Attendance Log"
          className="mb-3 mt-3!important"
          extra={
            <Button
              type="primary"
              size="small"
              icon={<Clock size={14} />}
              onClick={() => setShowAttendanceLog((prev) => !prev)}
              className="bg-primary text-white"
            >
              {showAttendanceLog ? 'Hide Logs' : 'View Logs'}
            </Button>
          }
        >
          {showAttendanceLog && (
            <>
              <div className="flex flex-wrap gap-2 mb-4">
                <Tag
                  color={attendanceFilter === null ? 'blue' : 'default'}
                  className="cursor-pointer"
                  onClick={() => setAttendanceFilter(null)}
                >
                  All
                </Tag>
                {attendanceFilterOptions.map((option) => (
                  <Tag
                    key={option.key}
                    color={attendanceFilter === option.key ? option.color : 'default'}
                    className="cursor-pointer"
                    onClick={() =>
                      setAttendanceFilter((prev) => (prev === option.key ? null : option.key))
                    }
                  >
                    {option.label}
                  </Tag>
                ))}
              </div>
              <Table
                columns={attendanceLogColumns}
                dataSource={filteredAttendanceLog}
                rowKey="_id"
                size="small"
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: 900 }}
              />
            </>
          )}
        </Card>
        <Divider />
        {/* Allowances */}
        <Card 
          title={
            <div className="flex justify-between items-center">
              <span>Allowances</span>
              {isEditing && (
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="small"
                  onClick={addNewAllowance}
                  className="!bg-green-600 !text-white"
                >
                  Add
                </Button>
              )}
            </div>
          } 
          className="mb-3"
        >
          <div className="space-y-3">
            {(isEditing ? allowances : payrollDetails?.allowances)?.map((allowance, index) => (
              <div key={allowance._id || index} className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} className="text-green-600" />
                  {isEditing ? (
                    <Input
                      value={allowance.name}
                      className="w-48"
                      placeholder="Allowance name"
                      onChange={(e) => handleAllowanceNameChange(index, e.target.value)}
                    />
                  ) : (
                    <span className="font-medium">{allowance.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <InputNumber
                      value={allowance.amount}
                      min={0}
                      precision={moneyPrecision}
                      className="w-32"
                      placeholder={moneyPrecision === 0 ? "0" : "0.00"}
                      onChange={(value) => handleAllowanceAmountChange(index, value)}
                    />
                  ) : (
                    <span className="font-bold text-green-700">
                      {formatCurrency(allowance.amount)}
                    </span>
                  )}
                  {isEditing && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      onClick={() => removeAllowance(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center p-3 bg-green-100 rounded-lg border border-green-200">
              <span className="font-bold">Total Allowances</span>
              <span className="font-bold text-green-800">
                {formatCurrency(isEditing ?
                  allowances.reduce((sum, item) => sum + (item.amount || 0), 0) :
                  payrollDetails?.salaryCalculations?.totalAllowances
                )}
              </span>
            </div>
          </div>
        </Card>
        <Divider />
        {/* Deductions */}
        <Card 
          title={
            <div className="flex justify-between items-center">
              <span>Deductions</span>
              {isEditing && (
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="small"
                  onClick={addNewDeduction}
                  className="!bg-red-600 !text-white"
                >
                  Add
                </Button>
              )}
            </div>
          } 
          className="mb-3"
        >
          <div className="space-y-3">
            {(isEditing ? deductions : payrollDetails?.deductions)?.map((deduction, index) => (
              <div key={deduction._id || index} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <TrendingDown size={16} className="text-red-600" />
                  {isEditing ? (
                    <Input
                      value={deduction.name}
                      className="w-48"
                      placeholder="Deduction name"
                      onChange={(e) => handleDeductionNameChange(index, e.target.value)}
                    />
                  ) : (
                    <span className="font-medium">{deduction.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isEditing ? (
                    <InputNumber
                      value={deduction.amount}
                      min={0}
                      precision={moneyPrecision}
                      className="w-32"
                      placeholder={moneyPrecision === 0 ? "0" : "0.00"}
                      onChange={(value) => handleDeductionAmountChange(index, value)}
                    />
                  ) : (
                    <span className="font-bold text-red-700">
                      {formatCurrency(deduction.amount)}
                    </span>
                  )}
                  {isEditing && (
                    <Button
                      type="text"
                      danger
                      size="small"
                      onClick={() => removeDeduction(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <div className="flex justify-between items-center p-3 bg-red-100 rounded-lg border border-red-200">
              <span className="font-bold">Total Deductions</span>
              <span className="font-bold text-red-800">
                {formatCurrency(isEditing ?
                  deductions.reduce((sum, item) => sum + (item.amount || 0), 0) :
                  payrollDetails?.salaryCalculations?.totalDeductions
                )}
              </span>
            </div>
          </div>
        </Card>


        {/* Action Buttons */}
        <div className="flex justify-end gap-3 py-3 ">
          {isEditing ? (
            <>
              <Button onClick={handleCancel} disabled={saving || savingAndProcessing}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
                disabled={savingAndProcessing}
                className="bg-primary text-white"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
              {payrollData.payrollDetails?.status === 'pending' && (
                <Button
                  type="primary"
                  onClick={handleSaveAndProcess}
                  loading={savingAndProcessing}
                  disabled={saving}
                  className="bg-primary text-white"
                >
                  {savingAndProcessing ? "Processing..." : "Save & Process"}
                </Button>
              )}
              {payrollData.payrollDetails?.status === 'processed' && (
                <Button
                  type="primary"
                  onClick={handleSaveAndProcess}
                  loading={savingAndProcessing}
                  disabled={saving}
                  className="bg-primary text-white"
                >
                  {savingAndProcessing ? "Approving..." : "Save & Approve"}
                </Button>
              )}
            </>
          ) : (
            <Button onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </Form>
    </div>
  );
};

export default PayrollEdit;